import { useEffect, useState } from "react";
import {
  DeleteButton,
  Edit,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { CustomActions } from '../../components';
import DeleteIcon from '@mui/icons-material/Delete';
import _ from 'lodash';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomToolbar = ({changedList, originList}) => {
  const record = useRecordContext();
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const backgroundColor = theme.palette.background.paper; // Default background color
  
  const addedItems = _.differenceBy(changedList, originList, "id");
  const deletedItems = _.differenceBy(originList, changedList, "id");
  const changedOrderItems = _.filter(changedList, (newItem) => {
    const originalItem = _.find(originList, { id: newItem.id });
    return originalItem && originalItem.order !== newItem.order;
  });
  const isDifferent = !_.isEqual(changedList, originList);
  const isDeletable = record.schedules.items.length === 0;

  const handleCancel = () => {
    redirect('list', 'routes');
  };

  const handleSuccessUpdate = async (res) => {
    try {
      if (isDifferent) {
        if (addedItems.length > 0) {
          addedItems.forEach(async (aItem) => {
            const data = {
              checkpoint_id: aItem.id,
              order: aItem.order,
              route_id: record.id,
            };
            await dataProvider.create("routeCheckPointss", {data});
          });
        }
        if (deletedItems.length > 0) {
          await dataProvider.deleteMany("routeCheckPointss", { ids: deletedItems.map((item) => item.rcpId) })
        }
        if (changedOrderItems.length > 0) {
          await Promise.all(
            changedOrderItems.map((cItem) => 
              dataProvider.update("routeCheckPointss", {
                id: cItem.rcpId,
                data: {
                  id: cItem.rcpId,
                  order: cItem.order,
                },
              })
            )
          )
        }
      }

      const id = res.id;
      const data = {
        id: res.id,
        name: res.name,
        route_type_id: res.route_type_id,
        site_id: res.site_id,
      };
      await dataProvider.update("routes", {id, data });

      notify('Route updated successfully', { type: 'success' });
      refresh();
      redirect('list', 'routes');
    } catch (error) {
      console.error('Error updating Route:', error);
      notify('Error updating Route', { type: 'error' });
    }
  };

  const handleSuccessDelete = async (res) => {
    try {
      if (res.route_checkpoints.items.length > 0) {
        res.route_checkpoints.items.forEach(async (item) => {
          const id = item.id;
          const previousData = item;
          await dataProvider.delete("routeCheckPointss", {id, previousData});
        });
      }

      const id = res.id;
      const previousData = res;
      await dataProvider.delete("routes", {id, previousData});
      notify('Route deleted successfully', { type: 'success' });
      redirect('list', 'routes');
    } catch (error) {
      console.error('Error deleting Route:', error);
      notify('Error deleting Route', { type: 'error' });
    }
  };

  return (
    <Toolbar
      sx={{
        backgroundColor, // Set the background color
      }}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display='flex' alignItems='center'>
          <DeleteButton
            type='button'
            size='small'
            label='Delete'
            sx={{textTransform: 'none', minWidth: '95px', minHeight: '30.75px'}}
            mutationOptions={{ onSuccess: handleSuccessDelete }}
            disabled={!isDeletable}
          />
          {!isDeletable && (
            <Tooltip
              title='This route is referenced by other schedules.'
              placement='top'
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </Tooltip>
          )}
        </Box>
        <Box>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              color: theme.palette.common.white, // Optional: primary color for cancel button text
              borderColor: theme.palette.primary.main, // Optional: primary color for cancel button border
              marginRight: theme.spacing(1),
              textTransform: 'none',
              minWidth: '95px',
            }}
            size='small'
          >
            Cancel
          </Button>
          <SaveButton
            alwaysEnable={isDifferent}
            type='button'
            label='Update'
            size='small'
            icon={null}
            sx={{textTransform: 'none', minWidth: '95px'}}
            mutationOptions={{ onSuccess: handleSuccessUpdate }}
          />
        </Box>
      </Box>
    </Toolbar>
  );
};

const CheckpointList = ({onChange, setOrigin}) => {
  const record = useRecordContext();

  // Fetch all checkpoints
  const { data: checkpoints, isLoading } = useGetList('checkpoints');
  const [routeCheckpoints, setRouteCheckpoints] = useState([]);

  useEffect(() => {
    if (checkpoints && checkpoints.length > 0 && record && record.route_checkpoints) {
      const rcpData = record.route_checkpoints.items.map((rcp) => {
        const checkpoint = checkpoints.find((cp) => cp.id === rcp.checkpoint_id);
        if (checkpoint) {
          return {
            id: checkpoint.id,
            name: checkpoint.name,
            order: rcp.order,
            rcpId: rcp.id,
          }
        }
      }).sort((a, b) => a.order - b.order);
      setRouteCheckpoints(rcpData);
      setOrigin(rcpData);
    }
  }, [checkpoints, record]);

  useEffect(() => {
    onChange(routeCheckpoints);
  }, [routeCheckpoints]);

  const handleCheckpointChange = (selectedcpId) => {
    if (selectedcpId) {
      const selectedCheckpoint = checkpoints.find((cp) => cp.id === selectedcpId);
      const isAlreadyAdded = routeCheckpoints.some((cp) => cp.id === selectedcpId);

      let updatedrcps;
      if (isAlreadyAdded) {
        // Remove the checkpoint from the list
        updatedrcps = routeCheckpoints.filter((cp) => cp.id !== selectedcpId);
        updatedrcps = updatedrcps.map((urcp, idx) => ({ ...urcp, order: idx }));
      } else {
        // Add the checkpoint to the list
        updatedrcps = [
          ...routeCheckpoints,
          { id: selectedCheckpoint.id, name: selectedCheckpoint.name, order: routeCheckpoints.length }
        ];
      }
      setRouteCheckpoints(updatedrcps);
    }
  };

  const handleDelete = (rcp) => {
    const updatedrcps = routeCheckpoints.filter((item) => item.id !== rcp.id);
    setRouteCheckpoints(updatedrcps.map((urcps, idx) => ({ ...urcps, order: idx })));
  };

  const moveUp = (rcp) => {
    if (rcp.order === 0) return;
    swapOrder(rcp, "up");
  };

  const moveDown = (rcp) => {
    if (rcp.order === routeCheckpoints.length - 1) return;
    swapOrder(rcp, "down");
  };

  const swapOrder = (rcp, orderStatus) => {
    if (orderStatus === "up") {
      const targetRcp = routeCheckpoints.find((item) => item.order === rcp.order - 1);
      setRouteCheckpoints([
        ...routeCheckpoints.filter((rcpItem) => rcpItem.id !== targetRcp.id && rcpItem.id !== rcp.id),
        {...rcp, order: rcp.order - 1},
        {...targetRcp, order: targetRcp.order + 1},
      ].sort((a, b) => a.order - b.order));
    } else if (orderStatus === "down") {
      const targetRcp = routeCheckpoints.find((item) => item.order === rcp.order + 1);
      setRouteCheckpoints([
        ...routeCheckpoints.filter((rcpItem) => rcpItem.id !== targetRcp.id && rcpItem.id !== rcp.id),
        {...rcp, order: rcp.order + 1},
        {...targetRcp, order: targetRcp.order - 1},
      ].sort((a, b) => a.order - b.order));
    }
  };
  
  if (isLoading) return <Typography variant="caption">Loading...</Typography>

  return (
    <div style={{ width: '100%'}} >
      {routeCheckpoints.length > 0 && routeCheckpoints.map((rcp, index, array) => (
        <div
          key={index}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: 'rgb(30, 30, 46)',
            border: '1px solid rgb(120, 120, 120)',
            marginTop: '10px',
          }}
        >
          <div>{index + 1}</div>
          <div
            style={{
              margin: 'auto',
              marginLeft: '10px'
            }}
          >{rcp.name}</div>
          <IconButton disabled={index === 0} onClick={() => moveUp(rcp)}>
            <KeyboardArrowUpIcon />
          </IconButton>
          <IconButton disabled={index === array.length - 1} onClick={() => moveDown(rcp)}>
            <KeyboardArrowDownIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(rcp)}>
            <DeleteIcon />
          </IconButton>
        </div>
      ))}
      {checkpoints.length > 0 && (
        <FormControl fullWidth>
          <InputLabel id="select-checkpoint">+ Add checkpoint</InputLabel>
          <Select
            labelId="select-checkpoint"
            value={''}
            onChange={(e) => handleCheckpointChange(e.target.value)}
          >
            {checkpoints.map((cp, idx) => <MenuItem value={cp.id} key={idx}>{cp.name}</MenuItem>)}
          </Select>
        </FormControl>
      )}
    </div>
  );
};

const EditRoute = () => {
  const [changedList, setChangedList] = useState([]);
  const [originList, setOriginList] = useState([]);

  return (
    <Card variant='elevation' sx={{ width: '50%' }}>
      <CardContent>
        <Edit actions={<CustomActions title="Edit Route" type='edit' />}>
          <SimpleForm toolbar={<CustomToolbar changedList={changedList} originList={originList} />} >
            <Typography variant="subtitle2">
              Details
            </Typography>
            <TextInput source="name" label='Name' fullWidth validate={[required('Name is required')]} />
            <ReferenceInput source="site_id" reference="sites" label="Site">
              <SelectInput optionText="name" fullWidth validate={[required('Site selection is required')]} />
            </ReferenceInput>
            <ReferenceInput source="route_type_id" reference="routeTypes" label="Route Type">
              <SelectInput optionText="name" fullWidth validate={[required('Route type selection is required')]} />
            </ReferenceInput>
            <Typography variant="subtitle2" gutterBottom>
              Checkpoint Selection
            </Typography>
            <CheckpointList onChange={setChangedList} setOrigin={setOriginList} />
          </SimpleForm>
        </Edit>
      </CardContent>
    </Card>
  )
}

export default EditRoute;
export { EditRoute };